import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import theme from './theme/theme';
import Layout from './components/layout/Layout';
import ScrollToTop from './components/common/ScrollToTop';
import Home from './pages/Home';
import About from './pages/About';
import Photos from './pages/Photos';
import Announcements from './pages/Announcements';
import Events from './pages/Events';
import Links from './pages/Links';
import Covenants from './pages/Covenants';
import BoardMembers from './pages/BoardMembers';
import RealEstateInfo from './pages/RealEstateInfo';
import DocumentViewer from './pages/DocumentViewer';
import ContactsPage from './pages/ContactsPage';
import PayHOA from './pages/PayHOA';
import UpdateContact from './pages/UpdateContact';
import ARCRequestForm from './pages/ARCRequestForm';
import NotFound from './pages/NotFound';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import CovenantComparison from './pages/CovenantComparison';
import Surveys from './pages/Surveys';
import SurveyForm from './pages/SurveyForm';
import NeighborBusinesses from './pages/NeighborBusinesses';
import BusinessSubmissionForm from './pages/BusinessSubmissionForm';
import AnnualMeetingSlideshow from './pages/AnnualMeetingSlideshow';
import Budget from './pages/Budget';

// Admin imports
import AdminLogin from './pages/admin/Login';

// Import the new AuthProvider from our hook
import { AuthProvider } from './hooks/useAuth';

// Track page views on route changes for Google Analytics
function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-M9PH36GHQC', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);
}

// Wrapper component to use the hook inside BrowserRouter
function PageTracker({ children }) {
  usePageTracking();
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <AuthProvider>
            <PageTracker>
              <ScrollToTop />
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Layout />}>
                  <Route index element={<Home />} />
                  <Route path="about" element={<About />} />
                  <Route path="photos" element={<Photos />} />
                  <Route path="announcements" element={<Announcements />} />
                  <Route path="events" element={<Events />} />
                  <Route path="links" element={<Links />} />
                  <Route path="covenants" element={<Covenants />} />
                  <Route path="covenant-comparison" element={<CovenantComparison />} />
                  <Route path="document/:documentName" element={<DocumentViewer />} />
                  <Route path="contacts" element={<ContactsPage />} />
                  <Route path="board-members" element={<BoardMembers />} />
                  <Route path="real-estate-info" element={<RealEstateInfo />} />
                  <Route path="pay-hoa" element={<PayHOA />} />
                  <Route path="update-contact" element={<UpdateContact />} />
                  <Route path="arc-request" element={<ARCRequestForm />} />
                  <Route path="surveys" element={<Surveys />} />
                  <Route path="surveys/:id" element={<SurveyForm />} />
                  <Route path="neighbor-businesses" element={<NeighborBusinesses />} />
                  <Route path="submit-business" element={<BusinessSubmissionForm />} />
                  <Route path="budget" element={<Budget />} />
                  <Route path="2025-annual-hoa-slideshow" element={<AnnualMeetingSlideshow />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="terms" element={<Terms />} />
                  <Route path="*" element={<NotFound />} />
                </Route>

                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
              </Routes>
            </PageTracker>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;